<template>
  <div class="p-4">
    <h2 class="text-xl font-bold mb-4">Organization Chart CMS</h2>

    <OrganizationChart
      :value="data"
      :selectionKeys="selection"
      @update:selectionKeys="(val) => (selection = val)"
      collapsible
      selectionMode="multiple"
    >
      <!-- Custom Node Template -->
      <template #person="{ node }">
        <div
          class="flex flex-col bg-white rounded shadow p-3 w-72 hover:shadow-lg transition"
        >
          <!-- Top row: image | details -->
          <div class="flex items-center gap-3">
            <img
              :alt="node.data.name"
              :src="node.data.image || defaultAvatar"
              class="w-16 h-16 rounded-lg object-cover border-2 border-gray-300 flex-shrink-0"
            />
            <div class="flex flex-col truncate items-start">
              <span
                class="font-semibold text-gray-800 truncate"
                :title="node.data.name"
              >
                {{ node.data.name }}
              </span>
              <span
                class="text-gray-500 text-sm truncate"
                :title="node.data.title"
              >
                {{ node.data.title }}
              </span>
            </div>
          </div>

          <!-- Controls row -->
          <div class="flex gap-2 mt-3 w-full">
            <button
              class="flex-1 px-2 py-1 text-xs rounded bg-blue-500 text-white"
              @click.stop="editNode(node)"
            >
              Edit
            </button>
            <button
              class="flex-1 px-2 py-1 text-xs rounded bg-green-500 text-white"
              @click.stop="addChild(node)"
            >
              Create
            </button>
            <button
              class="flex-1 px-2 py-1 text-xs rounded bg-red-500 text-white"
              @click.stop="deleteNode(node)"
            >
              Delete
            </button>
          </div>
        </div>
      </template>

      <!-- Default label for non-person nodes -->
      <template #default="{ node }">
        <div class="flex items-center gap-2 p-2 bg-gray-100 rounded shadow-sm">
          <span>{{ node.label }}</span>
        </div>
      </template>
    </OrganizationChart>

    <!-- Modal for Add/Edit -->
    <div
      v-if="modal.show"
      class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
    >
      <div class="bg-white p-6 rounded shadow-lg w-80">
        <h3 class="font-bold mb-4">
          {{ modal.isEdit ? "Edit Node" : "Add Node" }}
        </h3>
        <input
          v-model="modal.data.name"
          placeholder="Name"
          class="border p-1 w-full mb-2"
        />
        <input
          v-model="modal.data.title"
          placeholder="Title"
          class="border p-1 w-full mb-2"
        />
        <input
          v-model="modal.data.image"
          placeholder="Image URL"
          class="border p-1 w-full mb-4"
        />

        <div class="flex justify-end gap-2">
          <button @click="closeModal" class="px-3 py-1 rounded border">
            Cancel
          </button>
          <button
            @click="saveNode"
            class="px-3 py-1 rounded bg-blue-500 text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { ref } from "vue";
import OrganizationChart from "primevue/organizationchart";

export default {
  components: { OrganizationChart },
  setup() {
    const defaultAvatar =
      "https://primefaces.org/cdn/primevue/images/avatar/placeholder.png";

    const data = ref({
      key: "0",
      type: "person",
      data: {
        name: "Adrian Cruz",
        title: "IT Head",
        image: "https://randomuser.me/api/portraits/men/32.jpg",
      },
      children: [
        {
          key: "0_0",
          type: "person",
          data: {
            name: "Lara Santos",
            title: "Left Head",
            image: "https://i.pravatar.cc/150?u=lara-santos",
          },
          children: [
            {
              key: "0_0_0",
              data: {
                name: "Ethan Morales",
                title: "Sales",
                image: "https://i.pravatar.cc/150?u=ethan-morales",
              },
            },
            {
              key: "0_0_1",
              data: {
                name: "Maya Rivera",
                title: "Marketing",
                image: "https://i.pravatar.cc/150?u=maya-rivera",
              },
            },
          ],
        },
        {
          key: "0_1",
          type: "person",
          data: {
            name: "Carlos Valdez",
            title: "Center Head",
            image: "https://randomuser.me/api/portraits/men/33.jpg",
          },
          children: [
            {
              key: "0_1_0",
              data: {
                name: "Sofia Alvarez",
                title: "Development",
                image: "https://i.pravatar.cc/150?u=sofia-alvarez",
              },
            },
            {
              key: "0_1_1",
              data: {
                name: "Lucas Mendoza",
                title: "UI/UX Design",
                image: "https://i.pravatar.cc/150?u=lucas-mendoza",
              },
            },
          ],
        },
        {
          key: "0_2",
          type: "person",
          data: {
            name: "Isabella Torres",
            title: "Right Head",
            image: "https://i.pravatar.cc/150?u=isabella-torres",
          },
          children: [
            {
              key: "0_2_0",
              data: {
                name: "Diego Fernandez",
                title: "Accounting",
                image: "https://i.pravatar.cc/150?u=diego-fernandez",
              },
            },
            {
              key: "0_2_1",
              data: {
                name: "Valeria Cruz",
                title: "Finance Ops",
                image: "https://i.pravatar.cc/150?u=valeria-cruz",
              },
            },
          ],
        },
      ],
    });

    const selection = ref({});
    const modal = ref({
      show: false,
      isEdit: false,
      node: null,
      parent: null,
      data: { name: "", title: "", image: "" },
    });

    function addChild(node) {
      modal.value.show = true;
      modal.value.isEdit = false;
      modal.value.parent = node;
      modal.value.data = { name: "", title: "", image: "" };
    }
    function editNode(node) {
      modal.value.show = true;
      modal.value.isEdit = true;
      modal.value.node = node;
      modal.value.data = { ...node.data };
    }
    function deleteNode(node) {
      if (!confirm(`Delete ${node.data?.name || node.label}?`)) return;
      function removeRecursive(currentNode, targetKey) {
        if (!currentNode.children) return false;
        const index = currentNode.children.findIndex(
          (c) => c.key === targetKey
        );
        if (index !== -1) {
          currentNode.children.splice(index, 1);
          return true;
        }
        return currentNode.children.some((c) => removeRecursive(c, targetKey));
      }
      removeRecursive(data.value, node.key);
    }
    function saveNode() {
      if (modal.value.isEdit) modal.value.node.data = { ...modal.value.data };
      else {
        const parent = modal.value.parent;
        if (!parent.children) parent.children = [];
        const newKey = parent.key + "_" + parent.children.length;
        parent.children.push({
          key: newKey,
          type: "person",
          data: { ...modal.value.data },
          children: [],
        });
      }
      closeModal();
    }
    function closeModal() {
      modal.value.show = false;
      modal.value.node = null;
      modal.value.parent = null;
      modal.value.data = { name: "", title: "", image: "" };
    }

    return {
      data,
      selection,
      defaultAvatar,
      modal,
      addChild,
      editNode,
      deleteNode,
      saveNode,
      closeModal,
    };
  },
};
</script>

<style>
.p-organizationchart {
  max-height: 80vh;
  overflow: auto;
}
</style>
